import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContentCard from "@/components/base/ContentCard";
import { getAllItems, searchItems } from "@/utils/content";
import type { ContentType } from "@/types/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const typeFilters: { key: ContentType | "all"; label: string; icon: string }[] = [
  { key: "all", label: "全部", icon: "ri-apps-2-line" },
  { key: "plot", label: "剧情介绍", icon: "ri-book-open-line" },
  { key: "character", label: "角色分析", icon: "ri-user-heart-line" },
  { key: "news", label: "最新资讯", icon: "ri-newspaper-line" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const typeParam = (searchParams.get("type") || "all") as ContentType | "all";

  const [kw, setKw] = useState(q);

  usePageMeta(
    q ? `“${q}”的搜索结果 - 年轻的母亲3` : "搜索 - 年轻的母亲3",
    "在《年轻的母亲3》的剧情章节、角色档案与资讯中检索你关心的内容。",
  );

  const results = useMemo(() => searchItems(q, typeParam), [q, typeParam]);
  const recommendations = useMemo(() => getAllItems().slice(0, 3), []);

  const commitKeyword = (value: string) => {
    setKw(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const setType = (key: ContentType | "all") => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("type");
    else next.set("type", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-[1100px] mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <header className="mt-5 mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            全站搜索
          </h1>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            在全部剧情章节、角色档案与资讯中进行关键词检索，快速定位你想了解的内容。
          </p>
          <div className="mt-6 max-w-2xl">
            <div className="flex items-center w-full h-14 pl-5 pr-1.5 bg-background-50 border border-background-300 rounded-full focus-within:border-primary-400 transition-colors">
              <span className="w-5 h-5 mr-3 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-xl"></i>
              </span>
              <input
                type="search"
                name="q"
                value={kw}
                onChange={(e) => commitKeyword(e.target.value)}
                placeholder="搜索剧情、角色或资讯关键词…"
                className="flex-1 min-w-0 bg-transparent outline-none text-base text-foreground-900 placeholder:text-foreground-400"
              />
              {kw && (
                <button
                  type="button"
                  onClick={() => commitKeyword("")}
                  aria-label="清空"
                  className="shrink-0 w-9 h-9 mr-1 rounded-full flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
              <button
                type="button"
                onClick={() => commitKeyword(kw)}
                className="shrink-0 px-7 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {typeFilters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setType(f.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                typeParam === f.key
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={f.icon}></i></span>
              {f.label}
            </button>
          ))}
        </div>

        {q.trim() === "" ? (
          <div className="flex flex-col items-center justify-center text-center py-16 md:py-20 px-4 bg-background-50 border border-dashed border-background-300 rounded-lg">
            <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-5">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-900">开始你的检索</h3>
            <p className="mt-2 text-sm text-foreground-500 max-w-md leading-relaxed">
              输入关键词即可搜索《年轻的母亲3》的剧情、角色与资讯，例如“林静书”“天台”“结局”。
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground-500 mb-5">
              关键词 <span className="text-primary-700 font-medium">“{q}”</span> 共找到{" "}
              <span className="text-foreground-900 font-medium">{results.length}</span> 条结果
            </p>

            {results.length === 0 ? (
              <>
                <div className="flex flex-col items-center justify-center text-center py-16 md:py-20 px-4 bg-background-50 border border-dashed border-background-300 rounded-lg">
                  <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-5">
                    <i className="ri-emotion-sad-line text-3xl"></i>
                  </span>
                  <h3 className="font-heading font-bold text-lg text-foreground-900">没有找到相关内容</h3>
                  <p className="mt-2 text-sm text-foreground-500 max-w-md leading-relaxed">
                    换一个关键词试试，或浏览下方为你推荐的《年轻的母亲3》热门内容。
                  </p>
                </div>

                <section className="mt-10">
                  <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5">为你推荐</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {recommendations.map((item) => (
                      <div key={item.id} className="animate-soft-in">
                        <ContentCard item={item} />
                      </div>
                    ))}
                  </div>
                </section>
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {results.map((item) => (
                  <div key={item.id} className="animate-soft-in">
                    <ContentCard item={item} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}